import { css } from 'styled-components';

/**
 * Fragmentos de estilo do redesign reaproveitados pelas telas.
 *
 * Existem para o padrão visual ficar em um lugar só: um card, um input ou uma
 * pílula devem ter a mesma borda, raio e sombra em qualquer página. Alterar
 * aqui propaga para todas elas.
 */

/* Superfície padrão: cards, painéis, caixas de resumo. */
export const card = css`
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--r-md);
`;

/* Card clicável — o hover “levanta” o elemento. */
export const interactiveCard = css`
  ${card}
  cursor: pointer;
  transition: transform var(--dur-base) var(--ease-out),
    box-shadow var(--dur-base) var(--ease-out),
    border-color var(--dur-fast) var(--ease-standard);

  :hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
    border-color: var(--border-strong);
  }
`;

/* Base de qualquer controle de formulário (input, textarea, select). */
export const control = css`
  background-color: var(--surface);
  color: var(--ink);
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  transition: border-color var(--dur-fast) var(--ease-standard),
    box-shadow var(--dur-fast) var(--ease-standard);

  ::placeholder {
    color: var(--ink-faint);
  }

  :focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-tint);
  }

  :disabled {
    background-color: var(--surface-2);
    color: var(--ink-faint);
    cursor: not-allowed;
  }
`;

/* Botão redondo só de ícone (header, ações de card, fechar modal). */
export const iconButton = css`
  width: 3.8rem;
  height: 3.8rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--ink-soft);
  cursor: pointer;
  position: relative;
  transition: background-color var(--dur-fast) var(--ease-standard),
    color var(--dur-fast) var(--ease-standard);

  :hover {
    background-color: var(--surface-2);
    color: var(--ink);
  }
`;

/* Pílula de rótulo: categorias, status, filtros ativos. */
export const pill = css`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  border-radius: var(--r-pill);
  font-size: 1.2rem;
  font-weight: 600;
  padding: 0.5rem 1.2rem;
  white-space: nowrap;
`;

/* Cabeçalho de seção: título à esquerda, ação à direita. */
export const sectionHead = css`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  margin-bottom: 2.6rem;

  h2 {
    font-size: 2.6rem;
    font-weight: 700;
  }

  p {
    color: var(--ink-soft);
    font-size: 1.45rem;
    margin-top: 0.6rem;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

/* Rótulo pequeno em caixa alta acima de um título. */
export const eyebrow = css`
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-faint);
`;

/* Barra de rolagem discreta para trilhos horizontais. */
export const thinScrollbar = css`
  scrollbar-width: thin;
  scrollbar-color: var(--border-strong) transparent;

  ::-webkit-scrollbar {
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background-color: var(--border-strong);
    border-radius: var(--r-pill);
  }
`;

/* Menu lateral das áreas logadas (cortador e designer). */
export const sideNav = css`
  display: flex;
  flex-direction: column;
  min-width: 22rem;

  > h2 {
    font-size: 1.25rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--ink-faint);
    font-weight: 600;
    margin-bottom: 1.6rem;
  }

  ul {
    list-style: none;
  }

  /* O botão de ação do menu (virar PRO, novo projeto) vinha encostado no último
     item da lista — "Sair do sistema" — como se fosse mais um link. O respiro
     separa a navegação da ação. */
  > ul + button {
    margin-top: 2.4rem;
  }
`;

export const sideNavItem = css`
  display: flex;
  align-items: center;
  border-radius: var(--r-sm);
  transition: background-color var(--dur-fast) var(--ease-standard);

  :hover {
    background-color: var(--surface-2);
  }

  svg {
    flex: none;
    margin-left: 1.2rem;
  }

  a,
  button {
    flex: 1;
    height: auto;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
    text-decoration: none;
    font-size: 1.4rem;
    font-weight: 600;
    color: var(--ink-soft);
    padding: 1.2rem 1.2rem 1.2rem 1rem;
    transition: color var(--dur-fast) var(--ease-standard);
  }

  :hover a,
  :hover button {
    color: var(--ink);
  }

  /* Item da rota atual */
  &:has(a.active) {
    background-color: var(--accent-tint);

    a {
      color: var(--accent-deep);
    }
  }
`;

/**
 * Regras que valem só no tema escuro.
 *
 * Cobre os dois caminhos: a preferência do sistema (enquanto o visitante não
 * escolheu) e o `data-theme` gravado pelo botão do header. Use apenas quando o
 * token não resolve — imagens e logos de terceiros, por exemplo.
 */
/* `html` e não `:root`: o stylis do styled-components trata um seletor
   iniciado por `:` como pseudo-classe do próprio componente e monta
   `.classe:root ...` em vez do ancestral. */
export const darkOnly = rules => css`
  @media (prefers-color-scheme: dark) {
    html:not([data-theme='light']) & {
      ${rules}
    }
  }

  html[data-theme='dark'] & {
    ${rules}
  }
`;

/**
 * Giro dos indicadores de carregamento.
 *
 * O `@keyframes spin` é global (global.js). A regra de `prefers-reduced-motion`
 * zera a duração de toda animação, o que funciona para transições mas não para
 * um giro infinito: em vez de parar, o spinner passa a dar mil voltas por
 * segundo e vira um borrão tremendo. Aqui ele só desacelera.
 */
export const spinner = css`
  animation: spin var(--dur-spin) linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: var(--dur-spin-reduced) !important;
  }
`;
